<template>
	<article
		:id="containerId"
		:class="cssClass"
		:data-banner-type="bannerType"
	/>
</template>

<script setup lang="ts">
import { onMounted, watch, computed } from 'vue';
import { useAdManager } from '../composables/useAdManager';
import { useSearchStore } from '@stores/useSearchStore';
import type { BannerType } from '../types/google-tag.types';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { buildAdUnitPath, getSizeMappingForBanner } from '../utils/banner-positions';

const props = withDefaults(defineProps<{
	containerId: string;
	bannerType: BannerType;
	cssClass: string;
	clusterIndex?: number;
}>(), {
	clusterIndex: 0,
});

const { provider, orchestrator } = useAdManager();
const searchStore = useSearchStore();

const selectedDiscipline = computed<string | undefined>(() => {
	const disciplineFilter = searchStore.filtersSelection.find(
		(f) => f.type === FilterType.DISCIPLINES,
	);
	return disciplineFilter?.values?.[0]?.id;
});

onMounted(async () => {
	if (!import.meta.client) return;

	await provider.waitUntilReady();

	const isMobileSlot = props.bannerType === 'mobileAdvanced';
	const { defaultSize, entries } = getSizeMappingForBanner(props.bannerType, props.clusterIndex, isMobileSlot);

	orchestrator.defineAndRegisterSlot({
		bannerType: props.bannerType,
		adUnitPath: buildAdUnitPath(props.bannerType, props.containerId, isMobileSlot),
		defaultSize,
		containerId: props.containerId,
		sizeMappingEntries: entries,
	});

	orchestrator.displaySlot(props.containerId);

	await new Promise<void>((r) => queueMicrotask(r));

	orchestrator.refreshSlotsForType(props.bannerType as BannerType, props.containerId);
});

if (import.meta.client) {
	watch(selectedDiscipline, (discipline) => {
		orchestrator.updateTargetingAndRefresh(props.bannerType as BannerType, discipline);
	});
}
</script>
